import { Component, HostListener, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { UiLightboxService, resolveLightboxKind } from './ui-lightbox.service';

@Component({
  selector: 'ui-lightbox-host',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (lightbox.state().open) {
      <div
        class="ui-lightbox"
        role="dialog"
        aria-modal="true"
        [attr.aria-label]="current()?.caption || 'Visualização de arquivo'"
        (click)="onBackdropClick()">
        <button type="button" class="ui-lightbox__close" aria-label="Fechar" (click)="close()">×</button>

        @if (hasMultiple()) {
          <button type="button" class="ui-lightbox__nav ui-lightbox__nav--prev" aria-label="Anterior" (click)="prev($event)">‹</button>
          <button type="button" class="ui-lightbox__nav ui-lightbox__nav--next" aria-label="Próximo" (click)="next($event)">›</button>
        }

        <div
          class="ui-lightbox__stage"
          [class.ui-lightbox__stage--pdf]="isPdf()"
          (click)="$event.stopPropagation()">
          @if (current(); as item) {
            @if (isPdf()) {
              <iframe
                class="ui-lightbox__pdf"
                [src]="safePdfSrc(item.src)"
                [title]="item.caption || 'Documento PDF'"
                loading="lazy"></iframe>
            } @else {
              <img
                class="ui-lightbox__image"
                [src]="item.src"
                [alt]="item.alt || item.caption || 'Imagem'"
                (click)="$event.stopPropagation()" />
            }

            <div class="ui-lightbox__meta">
              @if (item.caption) {
                <p class="ui-lightbox__caption">{{ item.caption }}</p>
              }
              @if (isPdf()) {
                <a
                  class="ui-lightbox__open-tab"
                  [href]="item.src"
                  target="_blank"
                  rel="noopener noreferrer"
                  (click)="$event.stopPropagation()">
                  Abrir em nova aba
                </a>
              }
              @if (hasMultiple()) {
                <p class="ui-lightbox__counter">{{ lightbox.state().index + 1 }} / {{ lightbox.state().items.length }}</p>
              }
            </div>
          }
        </div>
      </div>
    }
  `,
  styles: [`
    .ui-lightbox {
      position: fixed;
      inset: 0;
      z-index: 12000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1.5rem;
      background: rgba(15, 23, 42, 0.88);
      backdrop-filter: blur(4px);
      animation: ui-lightbox-fade-in 0.18s ease-out;
    }

    @keyframes ui-lightbox-fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .ui-lightbox__stage {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      max-width: min(96vw, 72rem);
      max-height: 92vh;
      gap: 0.75rem;
    }

    .ui-lightbox__stage--pdf {
      width: min(96vw, 56rem);
      max-width: 96vw;
      height: min(88vh, 48rem);
      max-height: 88vh;
    }

    .ui-lightbox__image {
      max-width: 100%;
      max-height: calc(92vh - 4rem);
      width: auto;
      height: auto;
      object-fit: contain;
      border-radius: 0.5rem;
      box-shadow: 0 20px 50px rgba(0, 0, 0, 0.45);
      user-select: none;
    }

    .ui-lightbox__pdf {
      flex: 1;
      width: 100%;
      min-height: 0;
      height: calc(100% - 3.25rem);
      border: none;
      border-radius: 0.5rem;
      background: #fff;
      box-shadow: 0 20px 50px rgba(0, 0, 0, 0.45);
    }

    .ui-lightbox__meta {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.35rem;
      width: 100%;
      flex-shrink: 0;
    }

    .ui-lightbox__caption {
      margin: 0;
      max-width: 100%;
      padding: 0 0.5rem;
      font-size: 0.875rem;
      color: rgba(255, 255, 255, 0.92);
      text-align: center;
      line-height: 1.4;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .ui-lightbox__open-tab {
      font-size: 0.8125rem;
      color: rgba(255, 255, 255, 0.85);
      text-decoration: underline;
      text-underline-offset: 2px;
    }

    .ui-lightbox__open-tab:hover {
      color: #fff;
    }

    .ui-lightbox__counter {
      margin: 0;
      font-size: 0.75rem;
      color: rgba(255, 255, 255, 0.65);
    }

    .ui-lightbox__close {
      position: fixed;
      top: 1rem;
      right: 1rem;
      z-index: 2;
      width: 2.5rem;
      height: 2.5rem;
      border: none;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.12);
      color: #fff;
      font-size: 1.5rem;
      line-height: 1;
      cursor: pointer;
      transition: background 0.15s;
    }

    .ui-lightbox__close:hover {
      background: rgba(255, 255, 255, 0.22);
    }

    .ui-lightbox__nav {
      position: fixed;
      top: 50%;
      transform: translateY(-50%);
      z-index: 2;
      width: 2.75rem;
      height: 2.75rem;
      border: none;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.12);
      color: #fff;
      font-size: 1.75rem;
      line-height: 1;
      cursor: pointer;
      transition: background 0.15s;
    }

    .ui-lightbox__nav:hover {
      background: rgba(255, 255, 255, 0.22);
    }

    .ui-lightbox__nav--prev { left: 1rem; }
    .ui-lightbox__nav--next { right: 1rem; }

    @media (max-width: 640px) {
      .ui-lightbox { padding: 0.75rem; }
      .ui-lightbox__nav { display: none; }
      .ui-lightbox__image { max-height: calc(100vh - 5rem); border-radius: 0.25rem; }
      .ui-lightbox__stage--pdf {
        width: 100%;
        height: calc(100vh - 2rem);
      }
    }
  `],
})
export class UiLightboxHostComponent {
  readonly lightbox = inject(UiLightboxService);
  private readonly sanitizer = inject(DomSanitizer);

  readonly current = computed(() => {
    const s = this.lightbox.state();
    return s.items[s.index] ?? null;
  });

  readonly hasMultiple = computed(() => this.lightbox.state().items.length > 1);

  readonly isPdf = computed(() => {
    const item = this.current();
    return item ? resolveLightboxKind(item) === 'pdf' : false;
  });

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.lightbox.state().open) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.lightbox.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.lightbox.prev();
    }
  }

  safePdfSrc(url: string): SafeResourceUrl {
    const withViewer = url.includes('#') ? url : `${url}#view=FitH`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(withViewer);
  }

  close(): void {
    this.lightbox.close();
  }

  onBackdropClick(): void {
    this.close();
  }

  next(event: MouseEvent): void {
    event.stopPropagation();
    this.lightbox.next();
  }

  prev(event: MouseEvent): void {
    event.stopPropagation();
    this.lightbox.prev();
  }
}
